import { client, unwrap } from './client';
import { Patient, PatientPage } from '../types';

const PATIENT_LIST = '/patient/list';
const ADD_PATIENT = '/patient/add-patient';
const EDIT_PATIENT = '/patient/edit-patient';
const DELETE_PATIENT = '/patient/delete-patient/';
const PATIENT_FETCH = '/patient/';

function blankIfNA(value: any): string {
	if (!value || value === 'N/A') return '';
	return String(value);
}

export function normalizePatient(p: any): Patient {
	return {
		patient_id: p.id ?? p.patientId ?? '',
		first_name: p.firstName ?? '',
		last_name: p.lastName ?? '',
		email: blankIfNA(p.email),
		phone: blankIfNA(p.mobile),
		sex_at_birth: p.sexAtBirth ?? 'unknown',
		date_of_birth: p.dateOfBirth ?? '',
		patient_type: p.patientType,
		tags: p.tags ?? [],
		is_active: p.isActive ?? true,
	};
}

// Maps the flat create/edit payload back to batman's field names.
function toBatmanPayload(data: Partial<Patient>): Record<string, any> {
	return {
		firstName: data.first_name,
		lastName: data.last_name,
		email: data.email || '',
		mobile: data.phone || 'N/A',
	};
}

async function listOrSearch(
	searchText: string,
	limit: number,
	cursor: string | null,
): Promise<PatientPage> {
	const pageNumber = cursor ? Number(cursor) : 1;
	const res = await client.get(PATIENT_LIST, {
		params: { searchText, pageNumber, limit },
	});
	const body = unwrap<any>(res.data);
	const items = (body?.patientList ?? []).map(normalizePatient);
	const currentPage = body?.currentPage ?? pageNumber;
	const totalPages = body?.totalPages ?? 1;
	const next_cursor = currentPage < totalPages ? String(currentPage + 1) : null;
	return { items, next_cursor };
}

export const patientsService = {
	listPatients(limit = 25, cursor: string | null = null): Promise<PatientPage> {
		return listOrSearch('', limit, cursor);
	},
	searchPatients(q: string, limit = 20, cursor: string | null = null): Promise<PatientPage> {
		return listOrSearch(q, limit, cursor);
	},
	async createPatient(data: Partial<Patient>): Promise<Patient> {
		const res = await client.post(ADD_PATIENT, toBatmanPayload(data));
		return normalizePatient(unwrap<any>(res.data));
	},
	async getPatient(id: string): Promise<Patient> {
		const res = await client.get(`${PATIENT_FETCH}${id}`);
		return normalizePatient(unwrap<any>(res.data));
	},
	async updatePatient(id: string, data: Partial<Patient>): Promise<Patient> {
		const res = await client.post(EDIT_PATIENT, { patientId: id, ...toBatmanPayload(data) });
		return normalizePatient(unwrap<any>(res.data));
	},
	async deactivatePatient(id: string): Promise<void> {
		await client.delete(`${DELETE_PATIENT}${id}`);
	},
};
